(() => {
  const SELECTOR = '[data-pool-compare-video]';
  const initializedSections = new WeakMap();

  const initializeSection = (section) => {
    if (!section || initializedSections.has(section)) return;

    const videos = [...section.querySelectorAll('video')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileViewport = window.matchMedia('(max-width: 899px)');
    const revealItems = [...section.querySelectorAll('[data-pool-compare-reveal]')];
    const visibility = new Map(videos.map((video) => [video, 0]));
    let revealObserver;

    const hydrateVideo = (video) => {
      if (video.dataset.loaded === 'true') return;
      const template = video.parentElement?.querySelector('[data-pool-video-sources]');
      if (!template) return;

      video.appendChild(template.content.cloneNode(true));
      video.dataset.loaded = 'true';
      video.load();
    };

    const syncMobileReveals = () => {
      if (revealObserver) revealObserver.disconnect();
      revealObserver = undefined;
      section.classList.remove('is-mobile-reveal-ready');
      revealItems.forEach((item) => item.classList.remove('is-revealed'));

      if (!mobileViewport.matches || reducedMotion.matches) return;

      section.classList.add('is-mobile-reveal-ready');
      if (typeof IntersectionObserver === 'undefined') {
        revealItems.forEach((item) => item.classList.add('is-revealed'));
        return;
      }

      const nextRevealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            nextRevealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
      );
      revealObserver = nextRevealObserver;
      revealItems.forEach((item) => revealObserver.observe(item));
    };

    const pauseAll = () => videos.forEach((video) => video.pause());

    const updatePlayback = () => {
      if (document.visibilityState !== 'visible' || reducedMotion.matches) {
        pauseAll();
        return;
      }

      const activeVideo = videos.reduce((best, video) => {
        if (visibility.get(video) < 0.45) return best;
        return !best || visibility.get(video) > visibility.get(best) ? video : best;
      }, null);

      videos.forEach((video) => {
        if (video !== activeVideo) {
          video.pause();
          return;
        }

        video.muted = true;
        hydrateVideo(video);
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => video.pause());
      });
    };

    let observer;
    if (videos.length && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const visibleRatio = entry.isIntersecting && entry.intersectionRatio >= 0.45 ? entry.intersectionRatio : 0;
            visibility.set(entry.target, visibleRatio);
          });
          updatePlayback();
        },
        { threshold: [0, 0.45, 0.75, 1] },
      );
      videos.forEach((video) => observer.observe(video));
    }

    const onReducedMotionChange = () => {
      updatePlayback();
      syncMobileReveals();
    };

    syncMobileReveals();
    document.addEventListener('visibilitychange', updatePlayback);
    reducedMotion.addEventListener('change', onReducedMotionChange);
    mobileViewport.addEventListener('change', syncMobileReveals);

    initializedSections.set(section, () => {
      if (observer) observer.disconnect();
      if (revealObserver) revealObserver.disconnect();
      document.removeEventListener('visibilitychange', updatePlayback);
      reducedMotion.removeEventListener('change', onReducedMotionChange);
      mobileViewport.removeEventListener('change', syncMobileReveals);
      pauseAll();
      initializedSections.delete(section);
    });
  };

  const initializeAll = (root = document) => {
    if (root.matches?.(SELECTOR)) initializeSection(root);
    root.querySelectorAll?.(SELECTOR).forEach(initializeSection);
  };

  const start = () => initializeAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    initializedSections.get(section)?.();
  });
})();
